import type { Iso } from "./index.js";
import type { Via } from "./via.js";

export type { Via };

// ---------- OAuth scopes ----------

export const OMNI_SCOPES = ["omni.read", "omni.write"] as const;
export type OmniScope = (typeof OMNI_SCOPES)[number];

export const OMNI_SCOPE_DESCRIPTIONS: Readonly<Record<OmniScope, string>> = {
 "omni.read": "Read-only access to customers, catalog, templates, quotes, and orders",
 "omni.write": "Draft creation and contact updates for customers, contacts, and quotes",
};

// ---------- OAuth server constants ----------

export const OMNI_OAUTH_ISSUER = "https://omni.phoneware.cloud";
export const OMNI_OAUTH_RESOURCE_MCP = "https://omni.phoneware.cloud/mcp";
export const OMNI_OAUTH_AUDIENCE_V1 = "https://omni.phoneware.cloud/api/v1";
export const OMNI_OAUTH_AUDIENCE_MCP = "https://omni.phoneware.cloud/mcp";

export const OMNI_OAUTH_AUDIENCES = [
 "https://omni.phoneware.cloud",
 "https://omni.phoneware.cloud/api/v1",
 "https://omni.phoneware.cloud/mcp",
] as const;

export const IDENTITY_PLATFORM_ISSUER_PREFIX = "https://securetoken.google.com/";

// TTL policies
export const OAUTH_REQUEST_TTL_SECONDS = 600; // 10 minutes
export const OAUTH_CODE_TTL_SECONDS = 300; // 5 minutes
export const OAUTH_ACCESS_TOKEN_TTL_SECONDS = 3600; // 1 hour
export const OAUTH_REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 3600; // 30 days

// Rate limits
export const OAUTH_REGISTER_RATE_LIMIT = {
 windowMs: 60 * 60 * 1000,
 max: 20,
} as const;

export const OAUTH_TOKEN_RATE_LIMIT = {
 windowMs: 60 * 1000,
 max: 60,
} as const;

// ---------- OAuth record shapes (Firestore default database) ----------

/**
 * lens_oauth_clients/{clientId}
 * Registered OAuth 2.0 client record.
 */
export interface OAuthClientRecord {
 clientId: string;
 clientName: string;
 redirectUris: string[];
 clientUri?: string | null;
 logoUri?: string | null;
 scopes?: OmniScope[];
 grantTypes?: string[];
 responseTypes?: string[];
 tokenEndpointAuthMethod?: "none" | "client_secret_post" | "client_secret_basic";
 createdAt: Iso;
}

/**
 * RFC 7591 dynamic client registration shape used by netsapiens-mcp FirestoreClientsStore.
 */
export interface OAuthClientRegistration {
 client_id: string;
 client_name?: string;
 redirect_uris: string[];
 client_uri?: string;
 logo_uri?: string;
 scope?: string;
 grant_types?: string[];
 response_types?: string[];
 token_endpoint_auth_method?: string;
 created_at?: number;
}

/**
 * lens_oauth_requests/{id} (10 minutes TTL)
 * Authorization request awaiting user consent.
 */
export interface OAuthRequestRecord {
 id?: string;
 clientId: string;
 clientName: string;
 redirectUri: string;
 codeChallenge: string;
 state: string | null;
 scopes: OmniScope[];
 resource: string | null;
 createdAt: Iso;
 ttlAt: Date | Iso;
}

/**
 * lens_oauth_codes/{sha256(code)} (5 minutes TTL, single use by transaction)
 * One-time authorization code.
 */
export interface OAuthCodeRecord {
 requestId: string;
 clientId: string;
 email: string;
 uid: string;
 scopes: OmniScope[];
 codeChallenge: string;
 redirectUri: string;
 grantId: string;
 used: boolean;
 ttlAt: Date | Iso;
}

/**
 * lens_oauth_grants/{id}
 * Durable user authorization grant to a client.
 */
export interface OAuthGrantRecord {
 id: string;
 email: string;
 uid: string;
 clientId: string;
 clientName: string;
 redirectHost: string;
 scopes: OmniScope[];
 createdAt: Iso;
 lastUsedAt: Iso | null;
 revokedAt: Iso | null;
 revokedBy: string | null;
}

/**
 * lens_oauth_tokens/{sha256(token)}
 * Access token (1 hour) or refresh token (30 days, rotated on use).
 * Reusing a rotated refresh token revokes the grant.
 */
export interface OAuthTokenRecord {
 grantId: string;
 kind: "access" | "refresh";
 expiresAt: Iso;
 rotatedAt: Iso | null;
 ttlAt: Date | Iso;
}

/**
 * Standard RFC 6749 token endpoint response.
 */
export interface OAuthTokenResponse {
 access_token: string;
 token_type: "Bearer";
 expires_in: number;
 refresh_token?: string;
 scope: string;
}

/**
 * Verified token context attached after verifying an Omni access token.
 */
export interface VerifiedOAuthToken {
 grantId: string;
 email: string;
 uid: string;
 clientId: string;
 clientName: string;
 scopes: OmniScope[];
 issuer: string;
 audience: string;
 expiresAt: Iso;
}

// ---------- Pure scope checks and helper functions ----------

export function isOmniScope(value: unknown): value is OmniScope {
 return typeof value === "string" && (OMNI_SCOPES as readonly string[]).includes(value);
}

export function parseOmniScopes(value: unknown): OmniScope[] {
 if (Array.isArray(value)) {
  return value.filter(isOmniScope);
 }
 if (typeof value === "string") {
  return value
   .split(/\s+/)
   .map((s) => s.trim())
   .filter(isOmniScope);
 }
 return [];
}

export function hasScope(grantedScopes: readonly string[], requiredScope: OmniScope): boolean {
 return grantedScopes.includes(requiredScope);
}

export function canRead(grantedScopes: readonly string[]): boolean {
 return hasScope(grantedScopes, "omni.read");
}

export function canWrite(grantedScopes: readonly string[]): boolean {
 return hasScope(grantedScopes, "omni.write");
}

export function checkScope(
 grantedScopes: readonly string[],
 requiredScope: OmniScope,
): { ok: true } | { ok: false; error: string } {
 if (hasScope(grantedScopes, requiredScope)) {
  return { ok: true };
 }
 return {
  ok: false,
  error: `Missing required scope: ${requiredScope}`,
 };
}

export function refuseWithoutScope(
 grantedScopes: readonly string[],
 requiredScope: OmniScope,
): { ok: true } | { ok: false; error: string } {
 return checkScope(grantedScopes, requiredScope);
}

export function checkWriteAccess(
 grantedScopes: readonly string[],
): { ok: true } | { ok: false; error: string } {
 return checkScope(grantedScopes, "omni.write");
}

export function checkReadAccess(
 grantedScopes: readonly string[],
): { ok: true } | { ok: false; error: string } {
 return checkScope(grantedScopes, "omni.read");
}

export function isIssuerAllowed(issuer: string): boolean {
 if (!issuer) return false;
 if (issuer.startsWith(IDENTITY_PLATFORM_ISSUER_PREFIX)) {
  return false;
 }
 return issuer === OMNI_OAUTH_ISSUER;
}

export function isAudienceAllowed(audience: string): boolean {
 if (!audience) return false;
 return (OMNI_OAUTH_AUDIENCES as readonly string[]).includes(audience);
}

export function viaFromOAuthToken(
 client: { clientId: string; clientName: string },
 kind: "api" | "mcp",
): Via {
 return {
  kind,
  clientId: client.clientId,
  clientName: client.clientName,
 };
}
